import { memo, useMemo, Fragment } from 'react';
import useStyles from './styles';
import clsx from 'clsx';
import ContentLabel from 'atoms/ContentLabel';
import Image from 'next/image';
import { PostCardInfo } from 'types/post';
import { PostCategory } from 'types/category';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { ROUTES } from 'config/Nav';
import { BaseLink, Link, formatDate, cardSx, PostAuthor } from '@elevator/ui';

export interface PostCardProps extends PostCardInfo {
  /** Layout variant: 'main' for standard grid, 'small' for image-left 124px compact, 'featured' to fill parent for FeaturedPostGrid */
  variant?: 'main' | 'small' | 'featured';
  /** Whether to prioritize loading this image (for LCP optimization) */
  priority?: boolean;
}

/**
 * Custom comparison for {@link PostCard}'s React.memo. Exported for unit tests.
 */
export function postCardPropsAreEqual(
  prevProps: PostCardProps,
  nextProps: PostCardProps,
): boolean {
  if (
    prevProps.slug !== nextProps.slug ||
    prevProps.title !== nextProps.title ||
    prevProps.variant !== nextProps.variant ||
    prevProps.type !== nextProps.type ||
    prevProps.publishedAt !== nextProps.publishedAt ||
    prevProps.priority !== nextProps.priority ||
    prevProps.image?.sourceUrl !== nextProps.image?.sourceUrl
  ) {
    return false;
  }

  if (prevProps.authors.length !== nextProps.authors.length) {
    return false;
  }

  for (let i = 0; i < prevProps.authors.length; i++) {
    const prevAuthor = prevProps.authors[i];
    const nextAuthor = nextProps.authors[i];
    if (
      prevAuthor.preferredUsername !== nextAuthor.preferredUsername ||
      prevAuthor.displayName !== nextAuthor.displayName
    ) {
      return false;
    }
  }

  if (prevProps.categories.length !== nextProps.categories.length) {
    return false;
  }

  for (let i = 0; i < prevProps.categories.length; i++) {
    const prevCategory = prevProps.categories[i];
    const nextCategory = nextProps.categories[i];
    if (
      prevCategory.slug !== nextCategory.slug ||
      prevCategory.name !== nextCategory.name
    ) {
      return false;
    }
  }

  return true;
}

/**
 * PostCard Component
 *
 * Displays a post card with thumbnail image, title, categories, authors, and publication date.
 * Used in grid layouts to showcase multiple posts (homepage, category pages, etc.).
 *
 * Features:
 * - Responsive thumbnail image with post type badge overlay
 * - Category and author links
 * - Formatted publication date
 * - Supports different variants (main, small, featured) for different layouts
 * - Special styling for promoted posts (grey background)
 * - Memoized for performance optimization
 *
 * @param props - Component props
 * @param props.title - Post title
 * @param props.image - Post thumbnail image
 * @param props.slug - Post URL slug
 * @param props.type - Post type (Standard, Featured, Premiere, etc.)
 * @param props.authors - Array of post authors
 * @param props.categories - Array of post categories
 * @param props.publishedAt - Publication timestamp
 * @param props.variant - Layout variant ('main' for standard grid, 'small' for image-left 124px compact, 'featured' to fill parent for FeaturedPostGrid)
 */
const PostCard = memo(
  ({
    title,
    image,
    slug,
    type,
    authors,
    categories,
    publishedAt,
    variant = 'main',
    priority,
  }: PostCardProps) => {
    const { classes } = useStyles();

    /**
     * Renders category links with comma separators.
     * Memoized to prevent unnecessary re-renders when categories haven't changed.
     */
    const categoryElements = useMemo(() => {
      return categories.map((category: PostCategory, index) => (
        <Fragment key={category.slug}>
          <Link
            href={ROUTES.CATEGORY.fileApiPath}
            as={ROUTES.CATEGORY.to + category.slug}
            inline
          >
            <Typography
              component="span"
              variant="overline1"
              className={classes.categoryLink}
              display="inline"
            >
              {category.name}
            </Typography>
          </Link>
          {index !== categories.length - 1 && (
            <Typography
              component="span"
              variant="overline2"
              color="textSecondary"
              display="inline"
            >
              ,&nbsp;
            </Typography>
          )}
        </Fragment>
      ));
    }, [categories, classes.categoryLink]);

    /**
     * Renders author links with comma separators.
     * Memoized to prevent unnecessary re-renders when authors haven't changed.
     */
    const authorElements = useMemo(() => {
      return authors.map((author: PostAuthor, index: number) => (
        <Fragment key={author.preferredUsername}>
          <Link
            inline
            href={ROUTES.AUTHORS.to + author.preferredUsername}
            as={ROUTES.AUTHORS.to + author.preferredUsername}
          >
            <Typography
              variant="overline2"
              className={classes.authorLink}
              display="inline"
            >
              {author.displayName || author.preferredUsername}
            </Typography>
          </Link>
          {index !== authors.length - 1 && (
            <Typography variant="overline2" color="textSecondary" display="inline">
              ,&nbsp;
            </Typography>
          )}
        </Fragment>
      ));
    }, [authors, classes.authorLink]);

    // Determine container class name based on post type
    const containerClassName = useMemo(() => {
      return type === 'Promoted' ? classes.greyBackground : undefined;
    }, [type, classes.greyBackground]);

    // Determine padding styles based on variant
    const infoClassName = useMemo(() => {
      if (variant === 'small') return classes.smallInfoPadding;
      if (variant === 'featured') return classes.mainPadding;
      return classes.mainPadding;
    }, [variant, classes.mainPadding, classes.smallInfoPadding]);

    // Determine image container class based on variant
    const variantClassName = useMemo(() => {
      if (variant === 'small') return classes.smallImageContainer;
      if (variant === 'featured') return classes.featuredImageContainer;
      return classes.imageContainer;
    }, [
      variant,
      classes.imageContainer,
      classes.smallImageContainer,
      classes.featuredImageContainer,
    ]);

    return (
      <Box
        className={clsx(
          classes.root,
          variant === 'small' && classes.smallContainer,
          variant === 'featured' && classes.featuredContainer,
        )}
        data-component="PostCard"
      >
        <Paper
          square
          elevation={1}
          sx={cardSx}
          className={clsx(
            containerClassName,
            variant === 'small' && classes.smallCard,
            variant === 'featured' && classes.featuredCard,
          )}
        >
          <Box className={variantClassName}>
            <BaseLink href={ROUTES.POST.fileApiPath} as={`/${slug}`}>
              <Box className={classes.imageWrapper}>
                <Image
                  src={image.sourceUrl}
                  alt={`thumbnail for ${title}`}
                  fill
                  sizes="(max-width: 600px) 100vw, (max-width: 960px) 50vw, 33vw"
                  className={classes.thumbnailImage}
                  priority={Boolean(priority)}
                />
              </Box>
            </BaseLink>
            {type !== 'Standard' && (
              <Box className={classes.contentLabelContainer}>
                <ContentLabel type={type} />
              </Box>
            )}
          </Box>
          <Stack
            direction="column"
            className={clsx(classes.infoContainer, infoClassName)}
          >
            <Box
              className={clsx(
                classes.postMetaContainer,
                variant === 'small' && classes.smallPostMetaContainer,
              )}
            >
              <Stack
                direction="row"
                alignItems="center"
                className={classes.categoryRowContainer}
              >
                {categoryElements}
              </Stack>

              <Typography variant="overline2" color="textSecondary" display="inline">
                {formatDate(parseInt(String(publishedAt), 10), {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                })}{' '}
                | by&nbsp;
                {authorElements}
              </Typography>
            </Box>
            <Link href={ROUTES.POST.fileApiPath} as={`/${slug}`}>
              <Typography
                variant={variant === 'small' ? 'subtitle2' : 'h5'}
                component="h3"
                className={clsx(
                  variant === 'small' ? classes.smallTitleLink : classes.titleLink,
                )}
              >
                {title}
              </Typography>
            </Link>
          </Stack>
        </Paper>
      </Box>
    );
  },
  postCardPropsAreEqual,
);

PostCard.displayName = 'PostCard';

export default PostCard;
